// Marketing Hub — short links, click analytics, attribution overview
import { useState, useEffect, useMemo, useCallback } from "react";
import { watchMarketingLinks, watchClicksForLink, watchUserEventsForLink } from "../lib/marketingDb";
import { marketingCreateLinkCallable, marketingDeleteLinkCallable, marketingProbePublicRoutingCallable } from "../lib/crmApi";
import { ensureCallableAuthContext } from "../lib/crmCallableAuth";
import { resolveContactByUserRef } from "../lib/db";
import KpiCard from "../components/charts/KpiCard";
import Sparkline from "../components/charts/Sparkline";
import FunnelChart from "../components/charts/FunnelChart";
import Heatmap from "../components/charts/Heatmap";
import AlertBanner from "../components/AlertBanner";
import EmptyState from "../components/EmptyState";
import { MousePointerClick, Users, UserPlus, DollarSign, Percent, Link2 } from "lucide-react";

// Branded short links: r.scanbrix.com → Firebase Hosting → crmMarketingRedirect (/r/:slug).
// Root and other non-/r paths on r.scanbrix.com redirect to www.scanbrix.com (see public/index.html).
// www.scanbrix.com is Framer and cannot proxy /r/* without Framer Multi Site (paid).
const MARKETING_LINK_HOST = "r.scanbrix.com";
const PUBLIC_BASE =
  (typeof process !== "undefined" && process.env.REACT_APP_MARKETING_PUBLIC_ORIGIN) ||
  `https://${MARKETING_LINK_HOST}`;

const FIREBASE_HOSTING_CNAME = "alpine-realm-473117-f9.web.app";

function tsMs(v) {
  if (!v) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  return 0;
}

function formatUsd(cents) {
  const n = Number(cents) || 0;
  return `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function deviceBucket(ua) {
  const s = String(ua || "").toLowerCase();
  if (!s) return "—";
  if (/mobile|android|iphone|ipad|ipod/.test(s)) return "Mobile";
  if (/tablet|ipad/.test(s)) return "Tablet";
  return "Desktop";
}

function formatActivityLabel(name) {
  const labels = {
    click: "Click",
    signup: "Signup",
    marketing_touch: "Return visit",
    scan_uploaded: "Scan uploaded",
    order_created: "Order",
    payment_completed: "Payment",
  };
  return labels[name] || String(name || "—").replace(/_/g, " ");
}

function activityDetail(ev) {
  const meta = ev.metadata || {};
  if (ev.event_name === "order_created" && meta.order_id) return `Order ${String(meta.order_id).slice(0, 10)}`;
  if (ev.event_name === "payment_completed" && meta.amount_cents != null) {
    return formatUsd(meta.amount_cents);
  }
  if (ev.event_name === "scan_uploaded" && meta.scan_id) return `Scan ${String(meta.scan_id).slice(0, 10)}`;
  return "—";
}

function shortUrl(slug) {
  const base = String(PUBLIC_BASE).replace(/\/$/, "");
  return `${base}/r/${encodeURIComponent(slug)}`;
}

function clickSparkValues(clicks) {
  const map = new Map();
  for (const c of clicks) {
    const ms = tsMs(c.timestamp);
    if (!ms) continue;
    const d = new Date(ms);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    map.set(key, (map.get(key) || 0) + 1);
  }
  const keys = [...map.keys()].sort();
  return keys.slice(-14).map((k) => map.get(k) || 0);
}

export default function Marketing({ contacts = [] }) {
  const [links, setLinks] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [routing, setRouting] = useState({ loading: true, routed: null, server: null, err: null });
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState(null);
  const [activityLoadErr, setActivityLoadErr] = useState(null);

  const [form, setForm] = useState({
    slug: "",
    destination_type: "web",
    destination_value: "",
    web_fallback_url: "https://www.scanbrix.com",
    campaign: "",
    source: "",
    medium: "",
  });

  useEffect(() => {
    return watchMarketingLinks(setLinks, (e) => setLoadErr(e?.message || "Failed to load links"));
  }, []);

  const checkPublicRouting = useCallback(async () => {
    setRouting({ loading: true, routed: null, server: null, err: null });
    try {
      const auth = await ensureCallableAuthContext();
      if (!auth.ok) {
        setRouting({ loading: false, routed: null, server: null, err: auth.message });
        return;
      }
      const fn = marketingProbePublicRoutingCallable();
      const res = await fn({ origin: PUBLIC_BASE });
      const d = res.data || {};
      setRouting({
        loading: false,
        routed: !!d.routed,
        server: d.server || null,
        err: null,
      });
    } catch (e) {
      setRouting({
        loading: false,
        routed: null,
        server: null,
        err: e?.message || "Routing check failed",
      });
    }
  }, []);

  useEffect(() => {
    checkPublicRouting();
  }, [checkPublicRouting]);

  const selected = useMemo(() => links.find((l) => l.id === selectedId) || null, [links, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setClicks([]);
      setUserEvents([]);
      setDeleteErr(null);
      setActivityLoadErr(null);
      return;
    }
    const u1 = watchClicksForLink(selectedId, setClicks, (e) =>
      setActivityLoadErr(e?.message || "Failed to load click events")
    );
    const u2 = watchUserEventsForLink(selectedId, setUserEvents, (e) =>
      setActivityLoadErr(e?.message || "Failed to load attributed events")
    );
    return () => {
      u1();
      u2();
    };
  }, [selectedId]);

  const overview = useMemo(() => {
    let totalClicks = 0;
    let uniqueClicks = 0;
    let signups = 0;
    let revenue = 0;
    for (const l of links) {
      totalClicks += Number(l.total_clicks) || 0;
      uniqueClicks += Number(l.unique_clicks) || 0;
      signups += Number(l.signups) || 0;
      revenue += Number(l.revenue_cents) || 0;
    }
    const conv = uniqueClicks > 0 ? (signups / uniqueClicks) * 100 : 0;
    return { totalClicks, uniqueClicks, signups, revenue, conv };
  }, [links]);

  const ledgerKpi = useMemo(() => {
    let claimed = 0;
    let unknown = 0;
    const byPlacement = new Map();
    for (const c of contacts) {
      const acq = c.acquisition;
      if (acq?.handoff_id) {
        claimed += 1;
        const p = acq.cta_placement || "—";
        byPlacement.set(p, (byPlacement.get(p) || 0) + 1);
      } else {
        unknown += 1;
      }
    }
    return { claimed, unknown, byPlacement: [...byPlacement.entries()].sort((a, b) => b[1] - a[1]) };
  }, [contacts]);

  const agentRows = useMemo(() => {
    const by = new Map();
    for (const l of links) {
      const uid = String(l.created_by || "unknown");
      if (!by.has(uid)) {
        by.set(uid, { uid, revenue: 0, signups: 0, clicks: 0, links: 0 });
      }
      const row = by.get(uid);
      row.revenue += Number(l.revenue_cents) || 0;
      row.signups += Number(l.signups) || 0;
      row.clicks += Number(l.total_clicks) || 0;
      row.links += 1;
    }
    return [...by.values()].sort((a, b) => b.revenue - a.revenue);
  }, [links]);

  const funnel = useMemo(() => {
    if (!selected) return null;
    const evCounts = {};
    for (const e of userEvents) {
      const n = e.event_name || "";
      evCounts[n] = (evCounts[n] || 0) + 1;
    }
    return {
      clicks: Number(selected.total_clicks) || 0,
      unique: Number(selected.unique_clicks) || 0,
      signups: Number(selected.signups) || 0,
      scans: Number(selected.scan_uploads) || 0,
      orders: Number(selected.orders) || 0,
      payments: Number(selected.payments) || 0,
      revenue: Number(selected.revenue_cents) || 0,
      evCounts,
    };
  }, [selected, userEvents]);

  const topCountries = useMemo(() => {
    const m = new Map();
    for (const c of clicks) {
      const co = c.country || "—";
      m.set(co, (m.get(co) || 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [clicks]);

  const deviceMix = useMemo(() => {
    const m = new Map();
    for (const c of clicks) {
      const d = deviceBucket(c.user_agent);
      m.set(d, (m.get(d) || 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [clicks]);

  const recentActivity = useMemo(() => {
    const rows = [];
    for (const c of clicks) {
      const parts = [c.country, deviceBucket(c.user_agent)].filter((x) => x && x !== "—");
      rows.push({
        id: `click-${c.id}`,
        kind: "click",
        event_name: "click",
        user_id: c.user_id || null,
        visitor_id: c.visitor_id || null,
        timestamp: c.timestamp,
        detail: parts.length ? parts.join(" · ") : "Anonymous",
      });
    }
    for (const ev of userEvents) {
      rows.push({
        id: `event-${ev.id}`,
        kind: "event",
        event_name: ev.event_name,
        user_id: ev.user_id || null,
        timestamp: ev.timestamp,
        detail: activityDetail(ev),
        metadata: ev.metadata,
      });
    }
    return rows.sort((a, b) => tsMs(b.timestamp) - tsMs(a.timestamp)).slice(0, 40);
  }, [clicks, userEvents]);

  const copyText = useCallback(async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      window.prompt("Copy:", text);
    }
  }, []);

  const deleteSelectedLink = async () => {
    if (!selected) return;
    const slug = selected.slug;
    const ok = window.confirm(
      `Delete /${slug}? The short URL will stop working and click history for this link will be removed.`
    );
    if (!ok) return;

    setDeleteErr(null);
    setDeleting(true);
    try {
      const auth = await ensureCallableAuthContext();
      if (!auth.ok) {
        setDeleteErr(auth.message);
        return;
      }
      const fn = marketingDeleteLinkCallable();
      await fn({ linkId: selected.id });
      setSelectedId(null);
    } catch (e) {
      setDeleteErr(e?.message || "Failed to delete link");
    } finally {
      setDeleting(false);
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreateErr(null);
    setCreating(true);
    try {
      const auth = await ensureCallableAuthContext();
      if (!auth.ok) {
        setCreateErr(auth.message);
        setCreating(false);
        return;
      }
      const fn = marketingCreateLinkCallable();
      await fn({
        slug: form.slug.trim().toLowerCase(),
        destination_type: form.destination_type,
        destination_value: form.destination_value.trim(),
        web_fallback_url: form.destination_type === "deep_link" ? form.web_fallback_url.trim() : null,
        campaign: form.campaign.trim() || null,
        source: form.source.trim() || null,
        medium: form.medium.trim() || null,
      });
      setShowCreate(false);
      setForm({
        slug: "",
        destination_type: "web",
        destination_value: "",
        web_fallback_url: "https://www.scanbrix.com",
        campaign: "",
        source: "",
        medium: "",
      });
    } catch (err) {
      const code = err?.code || "";
      const msg = err?.message || String(err);
      if (code === "functions/unauthenticated" || /unauthenticated/i.test(msg)) {
        setCreateErr("Sign in with CRM login first, then try again. If you already did, hard-refresh (⌘⇧R).");
      } else {
        setCreateErr(msg);
      }
    } finally {
      setCreating(false);
    }
  };

  const resolveAgent = (uid) => {
    const c = resolveContactByUserRef(contacts, uid);
    return c ? c.name : uid.slice(0, 8) + "…";
  };

  const clickSpark = useMemo(() => clickSparkValues(clicks), [clicks]);

  const funnelStages = useMemo(() => {
    if (!funnel) return [];
    return [
      { label: "Clicks", value: funnel.clicks, color: "var(--secondary-500)" },
      { label: "Unique visitors", value: funnel.unique, color: "var(--purple)" },
      { label: "Signups", value: funnel.signups, color: "var(--primary-500)" },
      { label: "Scans", value: funnel.scans, color: "var(--info-500)" },
      { label: "Orders", value: funnel.orders, color: "var(--accent)" },
      {
        label: "Payments",
        value: funnel.payments,
        displayValue: formatUsd(funnel.revenue),
        color: "var(--success-500)",
      },
    ];
  }, [funnel]);

  return (
    <div className="marketing-hub page-wrap">
      <header className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1 className="page-header-title">Marketing Hub</h1>
          <p className="page-header-desc">
            Branded short links at <code>{PUBLIC_BASE}/r/:slug</code> and web→app hops at{" "}
            <code>{PUBLIC_BASE}/h/:id</code>. Ledger evidence lives on the contact.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + New link
        </button>
      </header>

      {loadErr && <AlertBanner variant="error">{loadErr}</AlertBanner>}

      {routing.loading ? (
        <div className="card card-sm" style={{ marginBottom: 16, color: "var(--text-2)", fontSize: 13 }}>
          Checking {PUBLIC_BASE}/r/* routing…
        </div>
      ) : routing.routed === false ? (
        <div className="card card-sm" style={{ borderColor: "var(--amber, #d97706)", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ fontWeight: 600, color: "var(--amber, #d97706)" }}>
              Branded links on {PUBLIC_BASE} are not wired up yet
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={checkPublicRouting}>
              Re-check
            </button>
          </div>
          <ol style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.6, margin: "0 0 12px 18px", padding: 0 }}>
            <li>
              <a href="https://console.firebase.google.com/project/alpine-realm-473117-f9/hosting/sites" target="_blank" rel="noopener noreferrer">
                Firebase Console → Hosting
              </a>
              {" "}→ site <code style={{ fontSize: 12 }}>alpine-realm-473117-f9</code> → <strong>Add custom domain</strong> →{" "}
              <code style={{ fontSize: 12 }}>{MARKETING_LINK_HOST}</code>
            </li>
            <li>
              Wix → Domains → scanbrix.com → DNS → add <strong>CNAME</strong>: host <code style={{ fontSize: 12 }}>r</code> →{" "}
              <code style={{ fontSize: 12 }}>{FIREBASE_HOSTING_CNAME}</code>{" "}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: 4, verticalAlign: "middle" }}
                onClick={() => copyText(FIREBASE_HOSTING_CNAME, "hosting-cname")}
              >
                {copied === "hosting-cname" ? "Copied" : "Copy"}
              </button>
            </li>
            <li>Wait for Firebase to verify DNS and provision SSL (often 15–60 min), then click Re-check</li>
          </ol>
          <p style={{ fontSize: 12, color: "var(--text-3)", margin: 0 }}>
            {routing.server ? (
              <>Last check: server header “{routing.server}” — not Firebase yet.</>
            ) : (
              <>
                <code style={{ fontSize: 12 }}>www.scanbrix.com/r/*</code> cannot work on Framer without Multi Site (paid). Use{" "}
                <code style={{ fontSize: 12 }}>{PUBLIC_BASE}/r/&lt;slug&gt;</code> instead.
              </>
            )}
          </p>
        </div>
      ) : routing.routed === true ? (
        <div className="card card-sm" style={{ borderColor: "var(--green, #16a34a)", marginBottom: 16, fontSize: 13, color: "var(--text-2)" }}>
          {PUBLIC_BASE}/r/* is routing to Firebase ({routing.server || "OK"}). Branded short links are live.
        </div>
      ) : routing.err ? (
        <div className="card card-sm" style={{ borderColor: "var(--amber, #d97706)", marginBottom: 16, fontSize: 13, color: "var(--text-2)" }}>
          Could not verify routing: {routing.err}{" "}
          <button type="button" className="btn btn-ghost btn-sm" onClick={checkPublicRouting}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="stats-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <KpiCard label="Total clicks" icon={MousePointerClick} value={overview.totalClicks.toLocaleString()} sparkline={clickSpark} />
        <KpiCard label="Unique visitors" icon={Users} value={overview.uniqueClicks.toLocaleString()} />
        <KpiCard label="Signups" icon={UserPlus} value={overview.signups.toLocaleString()} />
        <KpiCard label="Claimed bridges" icon={Link2} value={ledgerKpi.claimed.toLocaleString()} />
        <KpiCard label="Unattributed contacts" icon={Percent} value={ledgerKpi.unknown.toLocaleString()} />
        <KpiCard label="Revenue" icon={DollarSign} accent="success" value={formatUsd(overview.revenue)} />
        <KpiCard
          label="Signup rate"
          icon={Percent}
          value={`${overview.conv.toFixed(1)}%`}
          comparison="signups / unique visitors"
        />
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-3)", marginBottom: 12 }}>
          Agent / creator performance
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Creator</th>
                <th>Links</th>
                <th>Clicks</th>
                <th>Signups</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {agentRows.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ color: "var(--text-3)" }}>
                    No links yet.
                  </td>
                </tr>
              ) : (
                agentRows.map((r) => (
                  <tr key={r.uid}>
                    <td>{resolveAgent(r.uid)}</td>
                    <td>{r.links}</td>
                    <td>{r.clicks.toLocaleString()}</td>
                    <td>{r.signups.toLocaleString()}</td>
                    <td>{formatUsd(r.revenue)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card contacts-table-card">
        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-3)", marginBottom: 12 }}>
          Links
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Short link</th>
                <th>Type</th>
                <th>Clicks</th>
                <th>Unique</th>
                <th>Signups</th>
                <th>Revenue</th>
                <th>Conv %</th>
              </tr>
            </thead>
            <tbody>
              {links.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={Link2}
                      title="No links yet"
                      description="Create a branded short link to track clicks, signups, and revenue."
                      action={
                        <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
                          + New link
                        </button>
                      }
                    />
                  </td>
                </tr>
              ) : (
                links.map((l) => {
                  const uq = Number(l.unique_clicks) || 0;
                  const su = Number(l.signups) || 0;
                  const conv = uq > 0 ? ((su / uq) * 100).toFixed(1) : "0.0";
                  const url = shortUrl(l.slug);
                  const active = selectedId === l.id;
                  return (
                    <tr
                      key={l.id}
                      onClick={() => setSelectedId(l.id)}
                      className={active ? "row-selected" : ""}
                    >
                      <td>
                        <div style={{ fontWeight: 500 }}>/{l.slug}</div>
                        <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "var(--mono)" }}>{url}</div>
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: 11,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "var(--bg-elevated)",
                            border: "0.5px solid var(--border)",
                          }}
                        >
                          {l.destination_type || "web"}
                        </span>
                      </td>
                      <td>{Number(l.total_clicks) || 0}</td>
                      <td>{uq}</td>
                      <td>{su}</td>
                      <td>{formatUsd(l.revenue_cents)}</td>
                      <td>{conv}%</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="card" style={{ marginTop: 20 }}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Link detail</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{selected.slug}</div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 4, fontFamily: "var(--mono)" }}>{shortUrl(selected.slug)}</div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <button type="button" className="btn btn-sm" onClick={() => copyText(shortUrl(selected.slug), "main")}>
                {copied === "main" ? "Copied" : "Copy URL"}
              </button>
              <a
                className="btn btn-sm btn-primary"
                href={shortUrl(selected.slug)}
                target="_blank"
                rel="noreferrer"
              >
                Open
              </a>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                disabled={deleting}
                onClick={deleteSelectedLink}
              >
                {deleting ? "Deleting…" : "Delete link"}
              </button>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=96x96&data=${encodeURIComponent(shortUrl(selected.slug))}`}
                alt=""
                width={96}
                height={96}
                style={{ borderRadius: 8, border: "0.5px solid var(--border)" }}
              />
            </div>
          </div>

          {deleteErr && (
            <div style={{ marginBottom: 16, fontSize: 13, color: "var(--red)" }}>{deleteErr}</div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>Destination</div>
              <div style={{ marginTop: 6, wordBreak: "break-all", fontSize: 13 }}>{selected.destination_value}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>Campaign / UTM</div>
              <div style={{ marginTop: 6, fontSize: 13 }}>
                {[selected.campaign, selected.source, selected.medium].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
          </div>

          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-3)", marginBottom: 8 }}>
            Clicks (last ~14 days with data)
          </div>
          <div className="chart-card-header" style={{ marginTop: 8 }}>
            <div className="section-title">Clicks · last ~14 days</div>
          </div>
          {clickSpark.every((v) => v === 0) ? (
            <p className="text-caption">No clicks yet.</p>
          ) : (
            <Sparkline values={clickSpark} width={320} height={48} stroke="var(--purple)" fill="var(--purple)" />
          )}

          {funnelStages.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3 className="text-h4" style={{ marginBottom: 12 }}>Link funnel</h3>
              <FunnelChart stages={funnelStages} />
            </div>
          )}

          <div className="two-col" style={{ marginTop: 24 }}>
            <div className="card card-sm">
              <div className="section-title" style={{ marginBottom: 12 }}>Top countries</div>
              <Heatmap
                cells={topCountries.map(([co, n]) => ({ label: co, value: n }))}
                emptyLabel="No geo data on clicks yet."
              />
            </div>
            <div className="card card-sm">
              <div className="section-title" style={{ marginBottom: 12 }}>Devices</div>
              <Heatmap
                cells={deviceMix.map(([d, n]) => ({ label: d, value: n }))}
                emptyLabel="No device data yet."
              />
            </div>
          </div>

          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-3)", margin: "24px 0 8px" }}>
            Recent activity ({recentActivity.length})
          </div>
          {activityLoadErr && (
            <div style={{ marginBottom: 8, fontSize: 12, color: "var(--red)" }}>{activityLoadErr}</div>
          )}
          <div className="table-wrap" style={{ maxHeight: 240, overflow: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Who</th>
                  <th>Detail</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ color: "var(--text-3)", fontSize: 13, lineHeight: 1.5 }}>
                      No activity yet. Clicks show up here as soon as someone opens the short link. Signups, scans, and
                      revenue appear after the main app calls <code style={{ fontSize: 11 }}>marketingSyncAttribution</code>.
                    </td>
                  </tr>
                ) : (
                  recentActivity.map((row) => (
                    <tr key={row.id}>
                      <td>{formatActivityLabel(row.event_name)}</td>
                      <td style={{ fontSize: 12, color: "var(--text-2)" }}>
                        {row.user_id
                          ? resolveAgent(row.user_id)
                          : row.visitor_id
                            ? `${String(row.visitor_id).slice(0, 8)}…`
                            : "Anonymous"}
                      </td>
                      <td style={{ color: "var(--text-2)", fontSize: 12 }}>{row.detail}</td>
                      <td style={{ color: "var(--text-2)", fontSize: 12 }}>
                        {tsMs(row.timestamp) ? new Date(tsMs(row.timestamp)).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card card-sm" style={{ marginTop: 20, background: "var(--bg-surface)" }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Product integration</div>
        <p style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.55 }}>
          After login or signup in any Scanbrix app on <code style={{ fontSize: 12 }}>*.scanbrix.com</code>,{" "}
          <code style={{ fontSize: 12 }}>syncMarketingAttributionIfPresent</code> runs automatically (see{" "}
          <code style={{ fontSize: 12 }}>src/lib/marketingAttribution.js</code>). It reads{" "}
          <code style={{ fontSize: 12 }}>sb_mkt_lid</code> from the cookie or localStorage set by <code style={{ fontSize: 12 }}>/r/:slug</code>,
          then calls <code style={{ fontSize: 12 }}>marketingSyncAttribution</code>. That updates{" "}
          <code style={{ fontSize: 12 }}>users/{"{uid}"}.first_touch_link_id</code> / <code style={{ fontSize: 12 }}>last_touch_link_id</code> and
          records <code style={{ fontSize: 12 }}>user_events</code>. Revenue rolls up from <code style={{ fontSize: 12 }}>payments</code>; orders increment the orders funnel without duplicating revenue.
          The iOS app claims <code style={{ fontSize: 12 }}>/h/:handoff_id</code> via{" "}
          <code style={{ fontSize: 12 }}>acquisitionClaimHandoff</code> onto <code style={{ fontSize: 12 }}>users.acquisition</code>.
          Auth onCreate → Sign Ups Google Sheet is unchanged (default functions codebase).
        </p>
        <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 8 }}>
          Short links are always copied as <code style={{ fontSize: 12 }}>{PUBLIC_BASE}/r/&lt;slug&gt;</code>. Override with{" "}
          <code style={{ fontSize: 12 }}>REACT_APP_MARKETING_PUBLIC_ORIGIN</code> only if you use another scanbrix.com hostname.
        </p>
      </div>

      {showCreate && (
        <div className="modal-overlay" role="presentation" onClick={() => !creating && setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Create link</div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => !creating && setShowCreate(false)}>
                Close
              </button>
            </div>
            <form onSubmit={submitCreate}>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>Slug</div>
              <input
                className="input"
                style={{ width: "100%", marginBottom: 4 }}
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="e.g. demo or agent-delhi"
                required
              />
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 12 }}>
                URL will be {PUBLIC_BASE}/r/&lt;slug&gt;
              </div>

              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>Destination type</div>
              <select
                className="select"
                style={{ width: "100%", marginBottom: 12 }}
                value={form.destination_type}
                onChange={(e) => setForm((f) => ({ ...f, destination_type: e.target.value }))}
              >
                <option value="web">web</option>
                <option value="external">external</option>
                <option value="deep_link">deep_link</option>
              </select>

              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>Destination</div>
              <input
                className="input"
                style={{ width: "100%", marginBottom: 12 }}
                value={form.destination_value}
                onChange={(e) => setForm((f) => ({ ...f, destination_value: e.target.value }))}
                placeholder="https://… or scanbrix://home"
                required
              />

              {form.destination_type === "deep_link" && (
                <>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>Web fallback</div>
                  <input
                    className="input"
                    style={{ width: "100%", marginBottom: 12 }}
                    value={form.web_fallback_url}
                    onChange={(e) => setForm((f) => ({ ...f, web_fallback_url: e.target.value }))}
                  />
                </>
              )}

              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>Campaign (optional)</div>
              <input
                className="input"
                style={{ width: "100%", marginBottom: 12 }}
                value={form.campaign}
                onChange={(e) => setForm((f) => ({ ...f, campaign: e.target.value }))}
              />

              <div className="grid-2-col">
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>Source</div>
                  <input
                    className="input"
                    style={{ width: "100%" }}
                    value={form.source}
                    onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>Medium</div>
                  <input
                    className="input"
                    style={{ width: "100%" }}
                    value={form.medium}
                    onChange={(e) => setForm((f) => ({ ...f, medium: e.target.value }))}
                  />
                </div>
              </div>

              {createErr && (
                <div style={{ color: "var(--red)", fontSize: 13, marginTop: 12 }}>{createErr}</div>
              )}

              <div className="modal-footer" style={{ border: "none", paddingTop: 16 }}>
                <button type="button" className="btn" disabled={creating} onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? "Saving…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
